/**
 * YMM Blog Widget
 * Standalone Year/Make/Model vehicle selector for Shopify blog posts.
 * Does not require Liquid - configure entirely via JavaScript.
 *
 * Usage:
 *   <div id="ymm-widget"></div>
 *   <script src="https://cdn.shopify.com/.../assets/ymm-blog-widget.js"></script>
 *   <script>
 *     YMMWidget.init({
 *       container: '#ymm-widget',
 *       collectionId: 123456789,              // Shopify numeric collection ID
 *       // storeUrl: 'https://www.partcatalog.com',  // optional, defaults to current origin
 *       // heading:  'Find Parts for Your Vehicle',   // optional
 *     });
 *   </script>
 *
 * The collection ID never changes even if the handle/URL is renamed.
 * The widget resolves the current handle at runtime via /collections.json,
 * then redirects to:
 *   {storeUrl}/collections/{handle}?Year=YYYY&Make=Ford&Model=F-150
 */

(function () {
  'use strict';

  // ---- Config ----
  var DATA_URL = 'https://shopify.partcatalog.com/vehicles_index.json';
  var STORAGE_KEY = 'pc_ymm_v1';
  var CSS_INJECTED = false;
  var UID = 0;

  // ---- CSS (self-contained widget styles; PartCatalog design system) ----
  var WIDGET_CSS = [
    '.ymmw__wrapper {',
    '  background: var(--pc-surface, #fff);',
    '  border: 1px solid var(--pc-rule-outer, #d7dadc);',
    '  border-top: 2px solid var(--pc-ink, #17191b);',
    '  border-radius: var(--pc-radius, 2px);',
    '  padding: 20px;',
    '  margin: 20px 0;',
    '  max-width: 100%;',
    '  box-sizing: border-box;',
    '  font-family: var(--pc-font-body, "Barlow", "Helvetica Neue", Arial, sans-serif);',
    '  color: var(--pc-text, #2b3033);',
    '}',
    '.ymmw__heading {',
    '  font-family: var(--pc-font-structure, "Barlow Condensed", "Arial Narrow", sans-serif);',
    '  font-size: 16px;',
    '  font-weight: 700;',
    '  text-transform: uppercase;',
    '  letter-spacing: 0.08em;',
    '  color: var(--pc-ink, #17191b);',
    '  margin: 0 0 16px;',
    '}',
    /* Dropdowns layout */
    '.ymmw__dropdowns { display: flex; flex-direction: column; gap: 14px; }',
    '.ymmw__select-row { display: flex; flex-direction: column; gap: 12px; }',
    '@media (min-width: 600px) { .ymmw__select-row { flex-direction: row; gap: 12px; } }',
    '.ymmw__field { flex: 1; position: relative; }',
    /* Field labels - condensed uppercase, above each select */
    '.ymmw__label {',
    '  display: block;',
    '  font-family: var(--pc-font-structure, "Barlow Condensed", "Arial Narrow", sans-serif);',
    '  font-size: 13px;',
    '  font-weight: 600;',
    '  text-transform: uppercase;',
    '  letter-spacing: 0.08em;',
    '  color: var(--pc-muted, #6b7075);',
    '  text-align: left;',
    '  margin: 0 0 6px;',
    '}',
    '.ymmw__field.is-done .ymmw__label { color: var(--pc-ok, #2f7d4f); }',
    /* Hide native selects - stay in DOM as data source */
    '.ymmw__field select { position: absolute; opacity: 0; pointer-events: none; width: 0; height: 0; }',
    /* Custom DIV dropdown */
    '.ymmw-dd { position: relative; width: 100%; }',
    '.ymmw-dd__trigger {',
    '  display: flex;',
    '  align-items: center;',
    '  justify-content: space-between;',
    '  width: 100%;',
    '  padding: 9px 10px;',
    '  font-family: var(--pc-font-body, "Barlow", "Helvetica Neue", Arial, sans-serif);',
    '  font-size: 14px;',
    '  font-weight: 400;',
    '  color: var(--pc-text, #2b3033);',
    '  background: var(--pc-surface, #fff);',
    '  border: 1px solid var(--pc-rule-outer, #d7dadc);',
    '  border-radius: var(--pc-radius, 2px);',
    '  cursor: pointer;',
    '  transition: border-color 0.15s ease, box-shadow 0.15s ease;',
    '  user-select: none;',
    '  text-align: left;',
    '  box-sizing: border-box;',
    '}',
    '.ymmw-dd__trigger:hover { border-color: var(--pc-muted, #6b7075); }',
    '.ymmw-dd__trigger:focus-visible { outline: none; border-color: var(--pc-link, #1a4b8c); box-shadow: 0 0 0 3px rgba(26,75,140,0.5); }',
    '.ymmw-dd.is-open .ymmw-dd__trigger { border-color: var(--pc-link, #1a4b8c); box-shadow: 0 0 0 3px rgba(26,75,140,0.5); }',
    '.ymmw-dd.is-disabled .ymmw-dd__trigger { background: var(--pc-surface-panel, #f4f5f5); color: var(--pc-muted, #6b7075); border-color: var(--pc-rule-inner, #e9eaeb); cursor: not-allowed; }',
    '.ymmw-dd__placeholder { color: var(--pc-muted, #6b7075); }',
    '.ymmw-dd__chevron { width: 12px; height: 7px; flex-shrink: 0; margin-left: 10px; color: var(--pc-muted, #6b7075); }',
    '.ymmw-dd.is-open .ymmw-dd__chevron { transform: rotate(180deg); }',
    '.ymmw-dd.is-disabled .ymmw-dd__chevron { opacity: 0.4; }',
    '.ymmw-dd__list {',
    '  display: none;',
    '  position: absolute;',
    '  top: calc(100% + 4px);',
    '  left: 0;',
    '  right: 0;',
    '  max-height: 240px;',
    '  overflow-y: auto;',
    '  background: var(--pc-surface, #fff);',
    '  border: 1px solid var(--pc-rule-outer, #d7dadc);',
    '  border-radius: var(--pc-radius, 2px);',
    '  box-shadow: 0 6px 16px rgba(23,25,27,0.12);',
    '  z-index: 9999;',
    '  padding: 4px;',
    '  box-sizing: border-box;',
    '}',
    '.ymmw-dd.is-open .ymmw-dd__list { display: block; }',
    '.ymmw-dd__search {',
    '  display: block;',
    '  width: 100%;',
    '  padding: 8px 10px;',
    '  font-family: var(--pc-font-body, "Barlow", "Helvetica Neue", Arial, sans-serif);',
    '  font-size: 14px;',
    '  color: var(--pc-text, #2b3033);',
    '  border: 1px solid var(--pc-rule-outer, #d7dadc);',
    '  border-radius: var(--pc-radius, 2px);',
    '  margin-bottom: 4px;',
    '  outline: none;',
    '  box-sizing: border-box;',
    '}',
    '.ymmw-dd__search:focus { border-color: var(--pc-link, #1a4b8c); box-shadow: 0 0 0 3px rgba(26,75,140,0.5); }',
    '.ymmw-dd__item {',
    '  display: block;',
    '  width: 100%;',
    '  padding: 8px 10px;',
    '  font-family: var(--pc-font-body, "Barlow", "Helvetica Neue", Arial, sans-serif);',
    '  font-size: 14px;',
    '  font-weight: 400;',
    '  color: var(--pc-text, #2b3033);',
    '  background: none;',
    '  border: none;',
    '  border-radius: var(--pc-radius, 2px);',
    '  cursor: pointer;',
    '  text-align: left;',
    '  transition: background 0.1s;',
    '  box-sizing: border-box;',
    '}',
    '.ymmw-dd__item:hover, .ymmw-dd__item.is-focused { background: var(--pc-surface-panel, #f4f5f5); }',
    '.ymmw-dd__item.is-selected { font-weight: 600; background: var(--pc-ok-bg, #f1f8f3); color: var(--pc-ok, #2f7d4f); }',
    '.ymmw-dd__empty { padding: 10px 12px; font-size: 14px; color: var(--pc-muted, #6b7075); text-align: center; }',
    /* Search button - ink-fill CTA */
    '.ymmw__search-btn {',
    '  display: inline-flex;',
    '  align-items: center;',
    '  justify-content: center;',
    '  gap: 8px;',
    '  padding: 10px 18px;',
    '  font-family: var(--pc-font-structure, "Barlow Condensed", "Arial Narrow", sans-serif);',
    '  font-size: 14px;',
    '  font-weight: 600;',
    '  text-transform: uppercase;',
    '  letter-spacing: 0.08em;',
    '  color: #fff;',
    '  background: var(--pc-ink, #17191b);',
    '  border: none;',
    '  border-radius: var(--pc-radius, 2px);',
    '  cursor: pointer;',
    '  transition: background 0.15s ease;',
    '  white-space: nowrap;',
    '  align-self: stretch;',
    '}',
    '@media (min-width: 600px) { .ymmw__search-btn { align-self: flex-end; } }',
    '.ymmw__search-btn:hover:not(:disabled) { background: #000; }',
    '.ymmw__search-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(26,75,140,0.5); }',
    '.ymmw__search-btn:disabled { background: var(--pc-rule-outer, #d7dadc); color: var(--pc-muted, #6b7075); cursor: not-allowed; }',
    '.ymmw__btn-arrow { width: 15px; height: 15px; flex-shrink: 0; }',
    /* Loading state */
    '.ymmw__loading { font-size: 13px; color: var(--pc-muted, #6b7075); padding: 6px 0; }',
    /* Motion only when the user has not requested reduced motion */
    '@media (prefers-reduced-motion: no-preference) {',
    '  .ymmw__label { transition: color 0.2s ease; }',
    '  .ymmw-dd__chevron { transition: transform 0.2s ease; }',
    '}',
  ].join('\n');

  // ---- SVGs ----
  var CHEVRON_SVG = '<svg class="ymmw-dd__chevron" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ARROW_SVG   = '<svg class="ymmw__btn-arrow" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M4 12h16M13 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ---- Inject CSS once ----
  function injectCSS() {
    if (CSS_INJECTED) return;
    var style = document.createElement('style');
    style.textContent = WIDGET_CSS;
    document.head.appendChild(style);
    CSS_INJECTED = true;
  }

  // ---- Build HTML for a widget instance ----
  function buildHTML(heading, uid) {
    return [
      '<div class="ymmw__wrapper">',
      '  <div class="ymmw__heading">' + escapeHtml(heading) + '</div>',
      '  <div class="ymmw__dropdowns">',
      '    <div class="ymmw__select-row">',
      '      <div class="ymmw__field" data-step="make">',
      '        <label class="ymmw__label" for="ymmw-make-' + uid + '">Make</label>',
      '        <select id="ymmw-make-' + uid + '" data-ymm-make tabindex="-1" aria-hidden="true"><option value="">Select make</option></select>',
      '      </div>',
      '      <div class="ymmw__field" data-step="model">',
      '        <label class="ymmw__label" for="ymmw-model-' + uid + '">Model</label>',
      '        <select id="ymmw-model-' + uid + '" data-ymm-model tabindex="-1" aria-hidden="true" disabled><option value="">Select model</option></select>',
      '      </div>',
      '      <div class="ymmw__field" data-step="year">',
      '        <label class="ymmw__label" for="ymmw-year-' + uid + '">Year</label>',
      '        <select id="ymmw-year-' + uid + '" data-ymm-year tabindex="-1" aria-hidden="true" disabled><option value="">Select year</option></select>',
      '      </div>',
      '    </div>',
      '    <button type="button" class="ymmw__search-btn" data-ymm-search disabled>Find Parts' + ARROW_SVG + '</button>',
      '  </div>',
      '</div>',
    ].join('\n');
  }

  // ---- Escape HTML ----
  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ---- Storage helpers (shared across all instances) ----
  function saveVehicle(sel) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(sel)); } catch (e) {}
    document.cookie = 'pc_ymm=' + encodeURIComponent(JSON.stringify(sel)) +
      '; Max-Age=' + (60 * 60 * 24 * 30) + '; Path=/; SameSite=Lax';
  }
  function readVehicle() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    var m = document.cookie.match(/(?:^|;)\s*pc_ymm=([^;]+)/);
    if (m) { try { return JSON.parse(decodeURIComponent(m[1])); } catch (e) {} }
    return null;
  }

  // ---- Data helpers ----
  function allMakes(data) {
    var set = {};
    var byYear = (data && data.byYear) || {};
    Object.keys(byYear).forEach(function (y) {
      Object.keys(byYear[y] || {}).forEach(function (make) { set[make] = true; });
    });
    return Object.keys(set).sort(function (a, b) { return a.localeCompare(b); });
  }
  function modelsForMake(data, make) {
    var set = {};
    var byYear = (data && data.byYear) || {};
    Object.keys(byYear).forEach(function (y) {
      var list = (byYear[y] || {})[make];
      if (Array.isArray(list)) list.forEach(function (m) { set[m] = true; });
    });
    return Object.keys(set).sort(function (a, b) { return a.localeCompare(b); });
  }
  function yearsForMakeModel(data, make, model) {
    var out = [];
    var byYear = (data && data.byYear) || {};
    Object.keys(byYear).forEach(function (y) {
      var list = (byYear[y] || {})[make];
      if (Array.isArray(list) && list.indexOf(model) !== -1) out.push(+y);
    });
    return out.sort(function (a, b) { return b - a; });
  }

  // ---- Resolve collection handle from numeric ID ----
  // Uses Shopify's public AJAX API (/collections.json) - no auth token needed.
  // Result is cached in sessionStorage so subsequent page loads don't re-fetch.
  function resolveCollectionHandle(collectionId, storeUrl) {
    var cacheKey = 'pc_col_handle_' + collectionId;
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) return Promise.resolve(cached);
    } catch (e) {}

    var base = storeUrl.replace(/\/$/, '');
    var id   = Number(collectionId);

    function fetchPage(page) {
      return fetch(base + '/collections.json?limit=250&page=' + page)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var cols = (data && data.collections) || [];
          for (var i = 0; i < cols.length; i++) {
            if (cols[i].id === id) {
              try { sessionStorage.setItem(cacheKey, cols[i].handle); } catch (e) {}
              return cols[i].handle;
            }
          }
          // If a full page came back, there may be more
          if (cols.length === 250) return fetchPage(page + 1);
          return null;
        });
    }

    return fetchPage(1);
  }

  // ---- URL helpers ----
  function getQP(name) {
    var params = new URLSearchParams(location.search);
    var v = params.get(name);
    if (v == null) {
      params.forEach(function (val, k) {
        if (v == null && k.toLowerCase() === name.toLowerCase()) v = val;
      });
    }
    return v;
  }
  function readVehicleFromURL() {
    var Year  = getQP('Year')  || getQP('y')  || null;
    var Make  = getQP('Make')  || getQP('m')  || null;
    var Model = getQP('Model') || getQP('mo') || null;
    function dec(s) {
      if (!s) return null;
      return decodeURIComponent(String(s)).replace(/\+/g, ' ').trim() || null;
    }
    function titleCase(s) {
      if (!s) return null;
      return s.replace(/\s+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    }
    Year  = dec(Year);
    Make  = titleCase(dec(Make));
    Model = titleCase(dec(Model));
    if (Year && !/^\d{4}$/.test(Year)) Year = null;
    return { year: Year ? +Year : null, make: Make, model: Model };
  }

  // ---- Select helpers ----
  function populateSelect(selectEl, items, placeholder, valueFn) {
    selectEl.innerHTML = '';
    var opt0 = document.createElement('option');
    opt0.value = ''; opt0.textContent = placeholder;
    selectEl.appendChild(opt0);
    items.forEach(function (item) {
      var opt = document.createElement('option');
      opt.value = valueFn ? valueFn(item) : item;
      opt.textContent = item;
      selectEl.appendChild(opt);
    });
    selectEl.disabled = false;
  }
  function resetSelect(selectEl, placeholder) {
    selectEl.innerHTML = '';
    var opt0 = document.createElement('option');
    opt0.value = ''; opt0.textContent = placeholder;
    selectEl.appendChild(opt0);
    selectEl.disabled = true;
  }
  function setSelectValue(selectEl, value) {
    var opts = selectEl.options;
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].value.toLowerCase() === String(value).toLowerCase()) {
        selectEl.selectedIndex = i;
        return true;
      }
    }
    return false;
  }

  // ---- Custom dropdown ----
  function createCustomDropdown(selectEl) {
    var field = selectEl.closest('.ymmw__field');
    if (!field) return null;

    var dd = document.createElement('div');
    dd.className = 'ymmw-dd' + (selectEl.disabled ? ' is-disabled' : '');

    var labelEl = field.querySelector('label');
    var labelText = labelEl ? labelEl.textContent.trim() : '';

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'ymmw-dd__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    if (labelText) trigger.setAttribute('aria-label', labelText);
    var ph = (selectEl.options[0] && selectEl.options[0].text) || 'Select...';
    trigger.innerHTML = '<span class="ymmw-dd__placeholder">' + escapeHtml(ph) + '</span>' + CHEVRON_SVG;

    var listWrap = document.createElement('div');
    listWrap.className = 'ymmw-dd__list';

    var searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.className = 'ymmw-dd__search';
    searchInput.placeholder = 'Type to search...';
    searchInput.setAttribute('aria-label', 'Search ' + (labelText || 'options'));

    var itemsContainer = document.createElement('div');
    itemsContainer.className = 'ymmw-dd__items';
    itemsContainer.setAttribute('role', 'listbox');
    if (labelText) itemsContainer.setAttribute('aria-label', labelText);

    listWrap.appendChild(searchInput);
    listWrap.appendChild(itemsContainer);
    dd.appendChild(trigger);
    dd.appendChild(listWrap);
    field.appendChild(dd);

    function syncFromSelect() {
      var disabled = selectEl.disabled;
      dd.classList.toggle('is-disabled', disabled);
      var opts = Array.from(selectEl.options);
      var placeholder = (opts[0] && opts[0].text) || 'Select...';
      var selected = selectEl.value;
      var triggerSpan = trigger.querySelector('span');
      if (selected) {
        var selOpt = opts.find(function (o) { return o.value === selected; });
        triggerSpan.textContent = selOpt ? selOpt.text : selected;
        triggerSpan.className = '';
      } else {
        triggerSpan.textContent = placeholder;
        triggerSpan.className = 'ymmw-dd__placeholder';
      }
      itemsContainer.innerHTML = '';
      for (var i = 1; i < opts.length; i++) {
        (function (opt) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ymmw-dd__item' + (opt.value === selected ? ' is-selected' : '');
          btn.setAttribute('role', 'option');
          btn.setAttribute('aria-selected', opt.value === selected ? 'true' : 'false');
          btn.textContent = opt.text;
          btn.dataset.value = opt.value;
          btn.addEventListener('click', function () {
            selectEl.value = opt.value;
            selectEl.dispatchEvent(new Event('change'));
            close(true);
          });
          itemsContainer.appendChild(btn);
        })(opts[i]);
      }
      searchInput.value = '';
    }

    function open() {
      if (dd.classList.contains('is-disabled')) return;
      dd.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      searchInput.value = '';
      filterItems('');
      requestAnimationFrame(function () { searchInput.focus(); });
    }
    function close(returnFocus) {
      var wasOpen = dd.classList.contains('is-open');
      dd.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      searchInput.value = '';
      filterItems('');
      if (returnFocus && wasOpen) trigger.focus();
    }

    function filterItems(query) {
      var q = query.toLowerCase().trim();
      var items = itemsContainer.querySelectorAll('.ymmw-dd__item');
      var visible = 0;
      items.forEach(function (item) {
        var match = !q || item.textContent.toLowerCase().indexOf(q) !== -1;
        item.style.display = match ? '' : 'none';
        if (match) visible++;
      });
      var empty = itemsContainer.querySelector('.ymmw-dd__empty');
      if (visible === 0) {
        if (!empty) {
          empty = document.createElement('div');
          empty.className = 'ymmw-dd__empty';
          empty.setAttribute('role', 'status');
          empty.textContent = 'No matches';
          itemsContainer.appendChild(empty);
        }
        empty.style.display = '';
      } else if (empty) {
        empty.style.display = 'none';
      }
    }

    trigger.addEventListener('click', function () {
      dd.classList.contains('is-open') ? close() : open();
    });
    searchInput.addEventListener('input', function () { filterItems(searchInput.value); });

    dd.addEventListener('keydown', function (e) {
      if (!dd.classList.contains('is-open')) return;
      if (e.key === 'Escape') { close(true); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
      // Let Home/End move the caret inside the search field
      if ((e.key === 'Home' || e.key === 'End') && document.activeElement === searchInput) return;
      var items = Array.prototype.filter.call(
        itemsContainer.querySelectorAll('.ymmw-dd__item'),
        function (b) { return b.style.display !== 'none'; }
      );
      if (!items.length) return;
      var idx = items.indexOf(document.activeElement);
      var next;
      if (e.key === 'ArrowDown') next = idx < items.length - 1 ? items[idx + 1] : items[items.length - 1];
      else if (e.key === 'ArrowUp') next = idx > 0 ? items[idx - 1] : searchInput;
      else if (e.key === 'Home') next = items[0];
      else next = items[items.length - 1];
      e.preventDefault();
      next.focus();
    });

    document.addEventListener('click', function (e) {
      if (!dd.contains(e.target)) close();
    });

    var observer = new MutationObserver(syncFromSelect);
    observer.observe(selectEl, { childList: true, attributes: true, attributeFilter: ['disabled'] });
    selectEl.addEventListener('change', syncFromSelect);
    syncFromSelect();

    return { dd: dd, syncFromSelect: syncFromSelect, open: open, close: close };
  }

  // ---- Step state (presentation only: marks each field label done via --pc-ok) ----
  function updateStepper(el, selMake, selModel, selYear) {
    var fields = el.querySelectorAll('.ymmw__field');
    if (fields.length < 3) return;
    var hasMake  = !!selMake.value;
    var hasModel = !!selModel.value;
    var hasYear  = !!selYear.value;

    fields[0].classList.toggle('is-done', hasMake);
    fields[1].classList.toggle('is-done', hasMake && hasModel);
    fields[2].classList.toggle('is-done', hasMake && hasModel && hasYear);
  }

  // ---- Init a single widget instance ----
  function initInstance(container, collectionId, storeUrl, heading) {
    container.innerHTML = buildHTML(heading, ++UID);

    var el      = container.querySelector('.ymmw__wrapper');
    var selMake = container.querySelector('[data-ymm-make]');
    var selModel = container.querySelector('[data-ymm-model]');
    var selYear  = container.querySelector('[data-ymm-year]');
    var btnSearch = container.querySelector('[data-ymm-search]');

    var state = { data: null, handle: null };

    createCustomDropdown(selMake);
    createCustomDropdown(selModel);
    createCustomDropdown(selYear);

    function update() { updateStepper(el, selMake, selModel, selYear); }

    // Make → Model cascade
    selMake.addEventListener('change', function () {
      var make = selMake.value;
      btnSearch.disabled = true;
      if (!make) {
        resetSelect(selModel, 'Select model');
        resetSelect(selYear, 'Select year');
        update(); return;
      }
      populateSelect(selModel, modelsForMake(state.data, make), 'Select model');
      resetSelect(selYear, 'Select year');
      update();
    });

    // Model → Year cascade
    selModel.addEventListener('change', function () {
      var make  = selMake.value;
      var model = selModel.value;
      btnSearch.disabled = true;
      if (!model) {
        resetSelect(selYear, 'Select year');
        update(); return;
      }
      populateSelect(selYear, yearsForMakeModel(state.data, make, model), 'Select year', function (y) { return String(y); });
      update();
    });

    // Year → enable button
    selYear.addEventListener('change', function () {
      btnSearch.disabled = !selYear.value;
      update();
    });

    // Submit → redirect
    btnSearch.addEventListener('click', function () {
      var year  = selYear.value;
      var make  = selMake.value;
      var model = selModel.value;
      if (!year || !make || !model) return;

      saveVehicle({ year: +year, make: make, model: model });

      if (!state.handle) {
        console.error('[YMMWidget] Collection handle not resolved yet.');
        return;
      }

      var base = storeUrl.replace(/\/$/, '');
      var url  = base + '/collections/' + state.handle +
                 '?Year=' + encodeURIComponent(year) +
                 '&Make=' + encodeURIComponent(make) +
                 '&Model=' + encodeURIComponent(model);
      window.location.href = url;
    });

    // Boot: load vehicle data and resolve collection handle in parallel
    var loadingEl = document.createElement('div');
    loadingEl.className = 'ymmw__loading';
    loadingEl.setAttribute('role', 'status');
    loadingEl.textContent = 'Loading vehicles\u2026';
    el.appendChild(loadingEl);

    var vehiclePromise = fetch(DATA_URL, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });

    var handlePromise = resolveCollectionHandle(collectionId, storeUrl)
      .then(function (handle) {
        if (!handle) {
          console.error('[YMMWidget] Could not find collection with ID ' + collectionId + '. Check the ID and make sure the collection is published.');
        }
        state.handle = handle;
        return handle;
      });

    Promise.all([vehiclePromise, handlePromise])
      .then(function (results) {
        var json = results[0];
        state.data = json;
        loadingEl.parentNode && loadingEl.parentNode.removeChild(loadingEl);

        populateSelect(selMake, allMakes(state.data), 'Select make');

        // Pre-fill from URL params first, then localStorage
        var urlSel = readVehicleFromURL();
        var saved  = readVehicle();

        // Determine what to pre-fill (URL takes priority over localStorage)
        var pre = (urlSel.make) ? urlSel : (saved && saved.make ? saved : null);

        if (!pre) { update(); return; }

        var normMake = allMakes(state.data).find(function (m) {
          return m.toLowerCase() === (pre.make || '').toLowerCase();
        }) || pre.make;

        if (!normMake) { update(); return; }

        setSelectValue(selMake, normMake);
        selMake.dispatchEvent(new Event('change'));

        var models = modelsForMake(state.data, normMake);
        var normModel = models.find(function (m) {
          return m.toLowerCase() === (pre.model || '').toLowerCase();
        }) || null;

        if (!normModel) { update(); return; }

        populateSelect(selModel, models, 'Select model');
        setSelectValue(selModel, normModel);
        selModel.dispatchEvent(new Event('change'));

        var years = yearsForMakeModel(state.data, normMake, normModel);
        var normYear = years.find(function (y) { return y === +pre.year; }) || null;

        if (!normYear) { update(); return; }

        populateSelect(selYear, years, 'Select year', function (y) { return String(y); });
        setSelectValue(selYear, String(normYear));
        selYear.dispatchEvent(new Event('change'));

        update();
      })
      .catch(function (err) {
        if (loadingEl.parentNode) loadingEl.textContent = 'Could not load vehicle data. Please refresh.';
        console.error('[YMMWidget]', err);
      });
  }

  // ---- Public API ----
  var YMMWidget = {
    /**
     * Initialize the widget.
     *
     * @param {object} options
     * @param {string|Element} options.container    - CSS selector or DOM element
     * @param {number}         options.collectionId - Shopify numeric collection ID.
     *                         This never changes, even if the collection URL is renamed.
     *                         Find it in Shopify Admin → Collections → click the collection
     *                         → the number at the end of the URL is the ID.
     * @param {string}         [options.storeUrl]   - Base store URL (default: current origin)
     * @param {string}         [options.heading]    - Widget heading text
     */
    init: function (options) {
      if (!options || !options.collectionId) {
        console.error('[YMMWidget] options.collectionId is required.');
        return;
      }

      var container = options.container;
      if (typeof container === 'string') container = document.querySelector(container);
      if (!container) {
        console.error('[YMMWidget] Container element not found:', options.container);
        return;
      }

      injectCSS();

      var storeUrl = (options.storeUrl || window.location.origin).replace(/\/$/, '');
      var heading  = options.heading || 'Find This For Your Vehicle';

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          initInstance(container, options.collectionId, storeUrl, heading);
        });
      } else {
        initInstance(container, options.collectionId, storeUrl, heading);
      }
    }
  };

  window.YMMWidget = YMMWidget;

})();
